import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Header from '../../components/Header';
import api from '../../utils/api';
import { toLocalizedNumber, toLocalizedPrice } from '../../utils/localeUtils';
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  Legend, ResponsiveContainer
} from 'recharts';
import {
  FaChartLine, FaDollarSign, FaShoppingCart, FaBox,
  FaArrowUp, FaArrowDown, FaStar, FaUsers
} from 'react-icons/fa';
import './FarmerAnalytics.css';

const PIE_COLORS = ['#f59e0b', '#3b82f6', '#8b5cf6', '#06b6d4', '#22c55e', '#0d9488', '#ef4444'];

const REVENUE_STATUSES = ['delivered', 'received', 'completed'];

const FarmerAnalytics = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('month');
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [payments, setPayments] = useState([]);

  useEffect(() => {
    const ud = localStorage.getItem('user');
    if (ud) {
      try {
        const u = JSON.parse(ud);
        if (u.role !== 'farmer') { navigate('/'); return; }
        fetchData();
      } catch { setLoading(false); }
    } else { navigate('/login'); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [ordersRes, productsRes, paymentsRes] = await Promise.all([
        api.get('/orders'),
        api.get('/products/farmer/my-products'),
        api.get('/payments').catch(() => ({ data: [] }))
      ]);
      setOrders(ordersRes.data || []);
      setProducts(productsRes.data || []);
      setPayments(paymentsRes.data || []);
    } catch (err) {
      console.error('Error fetching analytics:', err);
    } finally { setLoading(false); }
  };

  const daysCut = { week: 7, month: 30, year: 365 }[timeRange];

  const analytics = useMemo(() => {
    const now = Date.now();
    const cutoff = now - daysCut * 86400000;
    const prevCutoff = now - daysCut * 2 * 86400000;

    const inRange = orders.filter(o => {
      const d = new Date(o.createdAt).getTime();
      return d >= cutoff && REVENUE_STATUSES.includes(o.orderStatus);
    });
    const prevRange = orders.filter(o => {
      const d = new Date(o.createdAt).getTime();
      return d >= prevCutoff && d < cutoff && REVENUE_STATUSES.includes(o.orderStatus);
    });

    const totalRevenue = inRange.reduce((s, o) => s + (o.totalPrice || 0), 0);
    const prevRevenue = prevRange.reduce((s, o) => s + (o.totalPrice || 0), 0);
    const totalOrders = inRange.length;
    const prevOrders = prevRange.length;

    const paidPayments = payments.filter(p =>
      ['completed', 'paid'].includes(p.paymentStatus?.toLowerCase()) &&
      new Date(p.paidAt || p.createdAt).getTime() >= cutoff
    );
    const totalPaid = paidPayments.reduce((s, p) => s + (p.amount || 0), 0);

    const uniqueCustomers = new Set(inRange.map(o => (o.consumer?._id || o.consumer || '').toString())).size;

    return {
      totalRevenue,
      totalOrders,
      avgOrderValue: totalOrders > 0 ? totalRevenue / totalOrders : 0,
      totalProducts: products.length,
      revenueChange: prevRevenue > 0 ? ((totalRevenue - prevRevenue) / prevRevenue * 100) : (totalRevenue > 0 ? 100 : 0),
      ordersChange: prevOrders > 0 ? ((totalOrders - prevOrders) / prevOrders * 100) : (totalOrders > 0 ? 100 : 0),
      totalPaid,
      uniqueCustomers,
    };
  }, [orders, products, payments, daysCut]);

  const salesData = useMemo(() => {
    const now = new Date();
    const intervals = timeRange === 'week' ? 7 : timeRange === 'month' ? 30 : 12;
    const intervalMs = (daysCut / intervals) * 86400000;
    const data = [];

    for (let i = intervals - 1; i >= 0; i--) {
      const start = new Date(now.getTime() - (i + 1) * intervalMs);
      const end = new Date(now.getTime() - i * intervalMs);

      const periodOrders = orders.filter(o => {
        if (!o.createdAt) return false;
        const d = new Date(o.createdAt);
        return d >= start && d < end && REVENUE_STATUSES.includes(o.orderStatus);
      });

      let label;
      if (timeRange === 'week') label = start.toLocaleDateString('en-US', { weekday: 'short' });
      else if (timeRange === 'month') label = start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      else label = start.toLocaleDateString('en-US', { month: 'short' });

      data.push({
        period: label,
        revenue: periodOrders.reduce((s, o) => s + (o.totalPrice || 0), 0),
        orders: periodOrders.length,
      });
    }
    return data;
  }, [orders, timeRange, daysCut]);

  const ordersByStatus = useMemo(() => {
    const counts = {};
    orders.forEach(o => {
      const s = o.orderStatus;
      if (s) counts[s] = (counts[s] || 0) + 1;
    });
    return Object.entries(counts)
      .filter(([, c]) => c > 0)
      .map(([name, value]) => ({ name: name.charAt(0).toUpperCase() + name.slice(1), value }));
  }, [orders]);

  const topProducts = useMemo(() => {
    const map = {};
    orders.forEach(o => {
      if (!o.items) return;
      o.items.forEach(item => {
        const id = item.product?._id || item.product || 'unknown';
        const name = item.name || item.product?.name || 'Unknown';
        const rev = (item.price || 0) * (item.quantity || 0);
        if (!map[id]) map[id] = { name, revenue: 0, quantity: 0 };
        map[id].revenue += rev;
        map[id].quantity += item.quantity || 0;
      });
    });
    return Object.values(map).sort((a, b) => b.revenue - a.revenue).slice(0, 6);
  }, [orders]);

  const monthlyTrend = useMemo(() => {
    const now = new Date();
    const data = [];
    for (let i = 11; i >= 0; i--) {
      const mStart = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const mEnd = new Date(now.getFullYear(), now.getMonth() - i + 1, 1);
      const rev = orders
        .filter(o => {
          if (!o.createdAt) return false;
          const d = new Date(o.createdAt);
          return d >= mStart && d < mEnd && REVENUE_STATUSES.includes(o.orderStatus);
        })
        .reduce((s, o) => s + (o.totalPrice || 0), 0);
      data.push({ month: mStart.toLocaleDateString('en-US', { month: 'short' }), revenue: rev });
    }
    return data;
  }, [orders]);

  const paymentBreakdown = useMemo(() => {
    const codCount = orders.filter(o => o.paymentMethod === 'COD').length;
    const onlineCount = orders.filter(o => o.paymentMethod === 'online').length;
    const result = [];
    if (codCount > 0) result.push({ name: 'COD', value: codCount });
    if (onlineCount > 0) result.push({ name: 'Online', value: onlineCount });
    return result;
  }, [orders]);

  if (loading) {
    return (
      <div className="fa-page"><Header />
        <div className="fa-loading"><div className="fa-dots"><span /><span /><span /></div><p>{t('farmerAnalytics.loading')}</p></div>
      </div>
    );
  }

  const changeBadge = (val) => {
    const positive = val >= 0;
    return (
      <span className={`fa-change ${positive ? 'fa-change--up' : 'fa-change--down'}`}>
        {positive ? <FaArrowUp /> : <FaArrowDown />}
        {Math.abs(val).toFixed(1)}%
      </span>
    );
  };

  return (
    <div className="fa-page">
      <Header />
      <div className="fa-container">

        {/* Header */}
        <div className="fa-head">
          <div>
            <h1 className="fa-title">{t('farmerAnalytics.title')}</h1>
            <p className="fa-subtitle">{t('farmerAnalytics.subtitle')}</p>
          </div>
          <div className="fa-range">
            {['week', 'month', 'year'].map(r => (
              <button key={r} className={`fa-range-btn ${timeRange === r ? 'fa-range-btn--on' : ''}`} onClick={() => setTimeRange(r)}>
                {t(`farmerAnalytics.${r}`)}
              </button>
            ))}
          </div>
        </div>

        {/* Metrics */}
        <div className="fa-metrics">
          {[
            { icon: <FaDollarSign />, label: t('farmerAnalytics.totalRevenue'), value: `Rs. ${toLocalizedPrice(analytics.totalRevenue)}`, change: analytics.revenueChange, cls: 'revenue' },
            { icon: <FaShoppingCart />, label: t('farmerAnalytics.totalOrders'), value: toLocalizedNumber(analytics.totalOrders), change: analytics.ordersChange, cls: 'orders' },
            { icon: <FaChartLine />, label: t('farmerAnalytics.avgOrderValue'), value: `Rs. ${toLocalizedPrice(analytics.avgOrderValue)}`, cls: 'avg' },
            { icon: <FaBox />, label: t('farmerAnalytics.totalProducts'), value: toLocalizedNumber(analytics.totalProducts), cls: 'products' },
            { icon: <FaUsers />, label: 'Customers', value: toLocalizedNumber(analytics.uniqueCustomers), cls: 'customers' },
            { icon: <FaStar />, label: 'Paid Revenue', value: `Rs. ${toLocalizedPrice(analytics.totalPaid)}`, cls: 'paid' },
          ].map(m => (
            <div key={m.cls} className="fa-metric">
              <div className={`fa-metric-ico fa-metric-ico--${m.cls}`}>{m.icon}</div>
              <div className="fa-metric-body">
                <span className="fa-metric-val">{m.value}</span>
                <span className="fa-metric-lbl">{m.label}</span>
                {m.change !== undefined && (
                  <div className="fa-metric-chg">
                    {changeBadge(m.change)}
                    <span className="fa-metric-vs">vs last {timeRange}</span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Row 1: Revenue & Orders + Order Status */}
        <div className="fa-row">
          <div className="fa-card fa-card--wide">
            <h3 className="fa-card-title">{t('farmerAnalytics.revenueOrdersOverTime')}</h3>
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={salesData}>
                <defs>
                  <linearGradient id="gRev" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#22c55e" stopOpacity={.35} />
                    <stop offset="100%" stopColor="#22c55e" stopOpacity={.02} />
                  </linearGradient>
                  <linearGradient id="gOrd" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#3b82f6" stopOpacity={.35} />
                    <stop offset="100%" stopColor="#3b82f6" stopOpacity={.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="period" stroke="#aaa" fontSize={12} />
                <YAxis yAxisId="left" stroke="#22c55e" fontSize={12} />
                <YAxis yAxisId="right" orientation="right" stroke="#3b82f6" fontSize={12} />
                <Tooltip formatter={(v, n) => [n === 'revenue' ? `Rs. ${toLocalizedPrice(v)}` : v, n === 'revenue' ? t('farmerAnalytics.revenue') : t('farmerAnalytics.orders')]} />
                <Legend />
                <Area yAxisId="left" type="monotone" dataKey="revenue" stroke="#22c55e" fill="url(#gRev)" strokeWidth={2.5} name={t('farmerAnalytics.revenue')} />
                <Area yAxisId="right" type="monotone" dataKey="orders" stroke="#3b82f6" fill="url(#gOrd)" strokeWidth={2.5} name={t('farmerAnalytics.orders')} />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          <div className="fa-card">
            <h3 className="fa-card-title">{t('farmerAnalytics.orderStatusDistribution')}</h3>
            {ordersByStatus.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie data={ordersByStatus} cx="50%" cy="50%" innerRadius={55} outerRadius={95} paddingAngle={3} dataKey="value"
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`} labelLine={false}>
                    {ordersByStatus.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="fa-empty-chart"><p>{t('farmerAnalytics.noOrderData')}</p></div>
            )}
          </div>
        </div>

        {/* Row 2: Top Products + Payment Breakdown */}
        <div className="fa-row">
          <div className="fa-card fa-card--wide">
            <h3 className="fa-card-title">{t('farmerAnalytics.top5Products')}</h3>
            {topProducts.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={topProducts} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis type="number" stroke="#aaa" fontSize={12} />
                  <YAxis dataKey="name" type="category" width={110} stroke="#aaa" fontSize={12} />
                  <Tooltip formatter={v => `Rs. ${toLocalizedPrice(v)}`} />
                  <Bar dataKey="revenue" fill="#22c55e" radius={[0, 8, 8, 0]} barSize={22} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="fa-empty-chart"><p>{t('farmerAnalytics.noProductSalesData')}</p></div>
            )}
          </div>

          <div className="fa-card">
            <h3 className="fa-card-title">Payment Method</h3>
            {paymentBreakdown.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie data={paymentBreakdown} cx="50%" cy="50%" innerRadius={55} outerRadius={95} paddingAngle={3} dataKey="value"
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`} labelLine={false}>
                    <Cell fill="#f59e0b" />
                    <Cell fill="#8b5cf6" />
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="fa-empty-chart"><p>No payment data</p></div>
            )}
          </div>
        </div>

        {/* Row 3: Monthly Trend (full width) */}
        <div className="fa-card">
          <h3 className="fa-card-title">{t('farmerAnalytics.twelveMonthTrend')}</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={monthlyTrend}>
              <defs>
                <linearGradient id="gLine" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#22c55e" stopOpacity={.2} />
                  <stop offset="100%" stopColor="#22c55e" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="month" stroke="#aaa" fontSize={12} />
              <YAxis stroke="#aaa" fontSize={12} />
              <Tooltip formatter={v => `Rs. ${toLocalizedPrice(v)}`} />
              <Legend />
              <Line type="monotone" dataKey="revenue" stroke="#22c55e" strokeWidth={3}
                dot={{ fill: '#22c55e', r: 4, strokeWidth: 0 }} activeDot={{ r: 7, fill: '#16a34a' }}
                name={t('farmerAnalytics.revenue')} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Top Products Table */}
        {topProducts.length > 0 && (
          <div className="fa-card">
            <h3 className="fa-card-title">Product Performance</h3>
            <div className="fa-tbl-wrap">
              <table className="fa-tbl">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Product</th>
                    <th>Quantity Sold</th>
                    <th>Revenue</th>
                    <th>Share</th>
                  </tr>
                </thead>
                <tbody>
                  {topProducts.map((p, i) => {
                    const totalRev = topProducts.reduce((s, x) => s + x.revenue, 0);
                    const share = totalRev > 0 ? (p.revenue / totalRev * 100) : 0;
                    return (
                      <tr key={i}>
                        <td className="fa-tbl-rank">{i + 1}</td>
                        <td className="fa-tbl-name">{p.name}</td>
                        <td>{toLocalizedNumber(p.quantity)}</td>
                        <td className="fa-tbl-rev">Rs. {toLocalizedPrice(p.revenue)}</td>
                        <td>
                          <div className="fa-tbl-bar-wrap">
                            <div className="fa-tbl-bar" style={{ width: `${share}%` }} />
                            <span>{share.toFixed(1)}%</span>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FarmerAnalytics;
